import { getDisplayDifference } from 'common/Widgets/PricingComparison/utils';
import { getDifferenceColor } from 'common/Widgets/valueDifferenceUtils';
import { VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';
import { AlertPricingType } from 'types/alerts';

export type PricingCellData = {
  marketRate: number | null;
  difference: number | null;
};

export type PricingRow = {
  key: string | number;
  communityId: number | null;
  communityName: string;
  isMyCommunity: boolean;
  roomTypeValues: Record<number, PricingCellData>;
};

export const getDifferenceLabel = (
  difference: number | null,
  competitorRate: number | null
): { color: string; text: string } | null => {
  const percentageDifference = getDisplayDifference(
    competitorRate,
    difference,
    VALUE_DIFFERENCE_DISPLAY.PERCENTAGES
  ) as number | null;

  if (percentageDifference === null) {
    return null;
  }

  if (percentageDifference === 0) {
    if (difference === null || difference === 0) {
      return null;
    }

    const isBelow = difference < 0;

    return {
      color: getDifferenceColor(difference),
      text: `<1% ${isBelow ? 'below' : 'above'}`,
    };
  }

  const isBelow = percentageDifference < 0;

  return {
    color: getDifferenceColor(percentageDifference),
    text: `${Math.abs(percentageDifference)}% ${isBelow ? 'below' : 'above'}`,
  };
};

export const buildRows = (pricingData: AlertPricingType) => {
  const roomTypes = pricingData?.room_types || [];
  const myCommunityRow: PricingRow = {
    key: 'my-community',
    communityId: null,
    communityName: 'My Community',
    isMyCommunity: true,
    roomTypeValues: {},
  };

  const competitorsById = new Map<number, PricingRow>();

  roomTypes.forEach((roomType) => {
    myCommunityRow.roomTypeValues[roomType.room_type_id] = {
      marketRate: roomType.market_rate,
      difference: null,
    };

    (roomType.competitors || []).forEach((competitor) => {
      if (!competitorsById.has(competitor.community_id)) {
        competitorsById.set(competitor.community_id, {
          key: competitor.community_id,
          communityId: competitor.community_id,
          communityName: competitor.community_name,
          isMyCommunity: false,
          roomTypeValues: {},
        });
      }

      const competitorRow = competitorsById.get(competitor.community_id) as PricingRow;
      competitorRow.roomTypeValues[roomType.room_type_id] = {
        marketRate: competitor.market_rate,
        difference: competitor.difference,
      };
    });
  });

  return {
    roomTypes,
    rows: [myCommunityRow, ...Array.from(competitorsById.values())],
  };
};
